// Public checklist validation page (/checklist/validate/:token) — the
// landing for reminder emails. No login: the token in the link is the
// auth. Two-pane issue-coordination layout: the left/main column keeps
// the context — title + project meta, the creation screenshot, the
// conversation thread, then the relevant slice of the schedule gantt
// (linked plan item highlighted) and notes; the right rail is a sticky
// properties/actions panel — current status, due date and assignee up
// top, then the composer: status (pending / in progress / complete),
// comment, images via file picker OR clipboard paste, the optional
// hand-off picker, and the post button. On small screens the rail
// stacks below the content. The thread keeps accumulating until the
// item is complete; the same link works the whole time.
import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Card, Col, Grid, Input, Result, Row, Segmented, Select,
  Skeleton, Tag, message,
} from "antd";
import { CheckCircleOutlined, PictureOutlined } from "@ant-design/icons";
import { url } from "api";
import { MEDIA_ACCEPT, mediaKindOf } from "components/Issue/IssueSourceControls";
import { ChecklistMediaChips, ChecklistThreadMedia } from "components/Checklist/ChecklistMediaPicker";
import CoordinationGantt from "components/CoordinationGantt/CoordinationGantt";
import { matchOption } from "components/SearchSelect/searchSelect";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In progress" },
  { value: "complete", label: "Complete" },
];
const STATUS_TAG = {
  open: "default",
  in_progress: "blue",
  complete: "green",
  // pending_review = the link user is done; only the owner's sign-off is
  // outstanding. To this page's audience that reads as "completed".
  pending_review: "green",
};
const STATUS_LABEL = { pending_review: "Completed — awaiting owner approval" };
const statusLabel = (s) => STATUS_LABEL[s] || String(s || "").replace("_", " ");

export default function ChecklistValidatePage({ token }) {
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState(localStorage.getItem("bl_validate_name") || "");
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState("in_progress");
  const [pendingShot, setPendingShot] = useState("");   // uploaded url awaiting post
  // Files of any kind attached for the next update (photo, video taken
  // on the phone, Excel / PDF / DWG) - optional, posted with the update.
  const [pendingMedia, setPendingMedia] = useState([]);
  const [assignEmail, setAssignEmail] = useState(undefined);
  const [assignName, setAssignName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [working, setWorking] = useState(false);
  const fileRef = useRef(null);
  const screens = Grid.useBreakpoint();

  // When the item was handed to a responsible person, their name is the
  // author of every update — never typed. Only unassigned items fall back
  // to the free-text name input (persisted in localStorage).
  const lockedName = item?.currentAssignee?.name?.trim() || "";
  const effectiveName = lockedName || name.trim();

  const load = () => {
    fetch(`${url}/checklist-validate/${token}`)
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
        setItem(body);
        if (body.status === "complete") setStatus("complete");
        else if (body.status === "in_progress") setStatus("in_progress");
      })
      .catch((e) => setError(e.message));
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  // Any accepted file goes through the media route; an image among them
  // also stands as the update's screenshot (older readers of the thread
  // and the mails look at that field).
  const uploadMedia = async (files) => {
    const list = [...(files || [])].filter((f) => f && mediaKindOf(f));
    if (!list.length) return [];
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of list) fd.append("files", f, f.name || "capture");
      fd.append("name", effectiveName || "validator");
      const r = await fetch(`${url}/checklist-validate/${token}/media`, { method: "POST", body: fd });
      const body = await r.json();
      if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
      const added = body.added || [];
      setPendingMedia((m) => [...m, ...added]);
      const img = added.find((a) => a.kind === "image");
      if (img && !pendingShot) setPendingShot(img.url);
      message.success(`${added.length} file${added.length === 1 ? "" : "s"} attached — they post with your update`);
      return added;
    } catch (e) {
      message.error(e.message || "Upload failed");
      throw e;
    } finally {
      setUploading(false);
    }
  };

  // Clipboard paste anywhere on the page.
  useEffect(() => {
    const onPaste = (e) => {
      const dt = e.clipboardData;
      const files = dt?.files && dt.files.length ? [...dt.files]
        : [...(dt?.items || [])].filter((it) => it.kind === "file").map((it) => it.getAsFile()).filter(Boolean);
      if (files.length) { e.preventDefault(); uploadMedia(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [token, name, lockedName]); // eslint-disable-line

  const post = async () => {
    if (!effectiveName) return message.warning("Please enter your name");
    if (!comment.trim() && !pendingShot && !pendingMedia.length && status === (item?.status === "open" ? "pending" : item?.status)) {
      return message.warning("Add a comment, a file, or change the status");
    }
    if (status === "complete" && item?.requireScreenshot && !pendingShot && !(item.screenshots || []).length && !(item.thread || []).some((t) => t.screenshotUrl)) {
      return message.warning("This item requires a screenshot of the work before it can be marked complete - attach or paste one first");
    }
    if (!lockedName) localStorage.setItem("bl_validate_name", name.trim());
    setWorking(true);
    try {
      const r = await fetch(`${url}/checklist-validate/${token}/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: effectiveName,
          comment: comment.trim(),
          status,
          screenshotUrl: pendingShot || undefined,
          attachments: pendingMedia,
          assignTo: assignEmail
            ? {
                email: assignEmail,
                name:
                  item.taggable?.find((t) => t.email === assignEmail)?.name ||
                  assignName ||
                  assignEmail,
              }
            : undefined,
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
      setComment("");
      setPendingShot("");
      setPendingMedia([]);
      setAssignEmail(undefined);
      setAssignName("");
      message.success("Update posted — the project team and schedule are updated");
      load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setWorking(false);
    }
  };

  if (error) {
    return (
      <div style={{ maxWidth: 720, margin: "60px auto", padding: 16 }}>
        <Result status="warning" title="This link is no longer valid" subTitle={error} />
      </div>
    );
  }
  if (!item) {
    return (
      <div style={{ maxWidth: 1100, margin: "40px auto", padding: 16 }}>
        <Skeleton active />
      </div>
    );
  }

  // pending_review closes the composer just like complete — the link
  // user's part is done. If the owner rejects, the item comes back as
  // in_progress and the composer reappears on the next load.
  const complete = item.status === "complete";
  const awaitingApproval = item.status === "pending_review";

  // Earliest screenshot = the one attached when the item was created;
  // surface it (and the thread) right under the title so the recipient
  // lands on context — the update form lives in the rail beside it.
  const creationShot = (item.screenshots || [])
    .slice()
    .sort((a, b) => new Date(a.uploadedAt || 0) - new Date(b.uploadedAt || 0))[0];

  const threadCard = (
    <Card size="small" title={`Updates (${item.thread?.length || 0})`} style={{ marginBottom: 16 }}>
      {(item.thread || []).length === 0 ? (
        <div style={{ color: "#999" }}>No updates yet — yours will be the first.</div>
      ) : (
        item.thread.map((t, i) => (
          <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid #f5f5f5" }}>
            <div style={{ fontSize: 13 }}>
              <b>{t.name}</b>
              {t.status && (
                <Tag color={STATUS_TAG[t.status] || "default"} style={{ marginLeft: 8 }}>
                  {statusLabel(t.status)}
                </Tag>
              )}
              <span style={{ color: "#aaa", marginLeft: 8, fontSize: 12 }}>
                {t.at ? new Date(t.at).toLocaleString() : ""}
              </span>
            </div>
            {t.comment && <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{t.comment}</div>}
            {t.assignedToEmail && (
              <div style={{ marginTop: 4, fontSize: 13 }}>
                <Tag color="purple">→ handed to {t.assignedToName || t.assignedToEmail}</Tag>
                <span style={{ color: "#999" }}>{t.assignedToEmail}</span>
              </div>
            )}
            <ChecklistThreadMedia attachments={t.attachments || []} screenshotUrl={t.screenshotUrl} />
          </div>
        ))
      )}
    </Card>
  );

  // Right rail, block 1: compact issue-tracker style properties.
  const propertiesCard = (
    <Card size="small" title="Properties" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
        <span style={{ color: "#888", fontSize: 13 }}>Status</span>
        <Tag color={STATUS_TAG[item.status] || "default"} style={{ marginRight: 0 }}>
          {statusLabel(item.status)}
        </Tag>
      </div>
      {item.dueDate && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
          <span style={{ color: "#888", fontSize: 13 }}>Due</span>
          <Tag style={{ marginRight: 0 }}>{String(item.dueDate).slice(0, 10)}</Tag>
        </div>
      )}
      {item.currentAssignee?.email && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, padding: "4px 0" }}>
          <span style={{ color: "#888", fontSize: 13 }}>Currently with</span>
          <span style={{ textAlign: "right", fontSize: 13 }}>
            <b>{item.currentAssignee.name || item.currentAssignee.email}</b>
            <div style={{ color: "#999", fontSize: 12 }}>{item.currentAssignee.email}</div>
          </span>
        </div>
      )}
    </Card>
  );

  // Right rail, blocks 2-6: the composer (hidden once complete).
  const composerCard = (
    <Card size="small" title="Post an update">
      {lockedName ? (
        <div style={{ marginBottom: 10, fontSize: 13, color: "#555" }}>
          Updating as <b>{lockedName}</b>
        </div>
      ) : (
        <Input placeholder="Your name *" value={name} style={{ marginBottom: 10 }}
               onChange={(e) => setName(e.target.value)} />
      )}
      <Segmented block options={STATUS_OPTIONS} value={status} onChange={setStatus}
                 style={{ marginBottom: 10 }} />
      {item?.requireScreenshot && (
        <Alert type={pendingShot || (item.screenshots || []).length || (item.thread || []).some((t) => t.screenshotUrl) ? "success" : "warning"} showIcon style={{ marginBottom: 10 }}
               message={pendingShot || (item.screenshots || []).length || (item.thread || []).some((t) => t.screenshotUrl)
                 ? "Screenshot provided - this item can be marked complete."
                 : "A screenshot of the work is required before this item can be marked complete."} />
      )}
      <Input.TextArea rows={4} placeholder="Comment — what was checked, what's left, blockers…"
                      value={comment} onChange={(e) => setComment(e.target.value)}
                      style={{ marginBottom: 10 }} />
      <div style={{ marginBottom: 10 }}>
        <Button block icon={<PictureOutlined />} loading={uploading}
                onClick={() => fileRef.current?.click()}>
          Attach photos, video or documents (optional)
        </Button>
        <input ref={fileRef} type="file" multiple accept={MEDIA_ACCEPT} style={{ display: "none" }}
               onChange={(e) => { uploadMedia(e.target.files); e.target.value = ""; }} />
        <div style={{ color: "#999", fontSize: 12, marginTop: 4 }}>
          A photo or a video taken now, the Excel / PDF / DWG of the check …or paste from your clipboard (Ctrl/Cmd+V). They post with your update.
        </div>
        {pendingMedia.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <ChecklistMediaChips items={pendingMedia} onRemove={(i) => { const next = pendingMedia.filter((_, j) => j !== i); setPendingMedia(next); if (!next.some((a) => a.url === pendingShot)) setPendingShot(""); }} size="normal" />
          </div>
        )}

      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
          This should be solved by (optional — they get this link by email and are added to the project's coordination):
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Select filterOption={matchOption}
            showSearch
            allowClear
            placeholder="Search people, or type an email"
            style={{ width: "100%" }}
            value={assignEmail}
            optionFilterProp="label"
            onChange={(v) => setAssignEmail(v)}
            onSearch={(txt) => {
              // typed free email: keep it selectable
            }}
            options={[
              ...(item.taggable || []).map((t) => ({
                value: t.email,
                label: `${t.name} — ${t.email}`,
              })),
            ]}
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ borderTop: "1px solid #eee", padding: 6, fontSize: 12, color: "#888" }}>
                  Not listed? Type the email in the box below.
                </div>
              </>
            )}
          />
          <Input placeholder="…or email"
                 value={assignEmail && !(item.taggable || []).some((t) => t.email === assignEmail) ? assignEmail : ""}
                 onChange={(e) => setAssignEmail(e.target.value.trim().toLowerCase() || undefined)} />
          <Input placeholder="Their name"
                 value={assignName}
                 onChange={(e) => setAssignName(e.target.value)} />
        </div>
      </div>
      <Button block type="primary" size="large" loading={working} onClick={post}>
        Post update
      </Button>
    </Card>
  );

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px", fontFamily: "Inter, system-ui, sans-serif" }}>
      <Row gutter={24}>
        {/* Left / main column: the context — history first, so the
            recipient lands on it, not on an empty form. */}
        <Col xs={24} md={14} lg={16}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 26 }}>{item.title}</h1>
            <Tag color={STATUS_TAG[item.status] || "default"} style={{ fontSize: 13 }}>
              {statusLabel(item.status)}
            </Tag>
          </div>
          <div style={{ color: "#888", marginBottom: 16, fontSize: 14 }}>
            Project: <b>{item.projectTitle}</b>
            {item.planItem && <> · Activity: <Tag color="blue">{item.planItem.name}</Tag></>}
            {item.dueDate && <> · Due <Tag>{String(item.dueDate).slice(0, 10)}</Tag></>}
          </div>

          {creationShot && (
            <a href={`${url}${creationShot.url}`} target="_blank" rel="noreferrer">
              <img src={`${url}${creationShot.url}`} alt="screenshot from creation"
                   style={{ display: "block", maxWidth: 480, width: "100%", marginBottom: 16, borderRadius: 6, border: "1px solid #eee" }} />
            </a>
          )}

          {threadCard}

          {item.planWindow?.length > 0 && (
            <Card size="small" style={{ marginBottom: 16, overflowX: "auto" }}
                  title="Where this sits in the schedule (highlighted)">
              <CoordinationGantt
                items={item.planWindow.map((i) => ({
                  ...i,
                  healthStatus: i.highlight ? "at_risk" : undefined,
                  issueCounts: {},
                }))}
              />
            </Card>
          )}

          {item.notes && (
            <Alert type="info" style={{ marginBottom: 16, whiteSpace: "pre-wrap" }} message={item.notes} />
          )}
        </Col>

        {/* Right / action rail: sticky properties + composer panel
            (normal flow when stacked on small screens). */}
        <Col xs={24} md={10} lg={8}>
          <div style={{ position: screens.md ? "sticky" : "static", top: 16 }}>
            {propertiesCard}
            {complete || awaitingApproval ? (
              <Card size="small">
                <Result
                  status="success"
                  icon={<CheckCircleOutlined />}
                  title={awaitingApproval ? "Your completion is recorded" : "This item is complete"}
                  subTitle={
                    awaitingApproval
                      ? "Thanks — the item is marked completed and is with the project owner for approval. If they need anything more, this link reopens and you'll be able to post again."
                      : "Thanks — the project checklist, master plan and schedule reflect it. The project team can reopen it if more work is needed."
                  }
                />
              </Card>
            ) : (
              composerCard
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
}
